import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import CaseContextLine from 'src/components/SupportCase/CaseContextLine';
import CaseStream from 'src/components/SupportCase/CaseStream';
import Composer from 'src/components/SupportCase/Composer';
import PromptFooter from 'src/components/SupportCase/PromptFooter';
import useStartCase, {
  newClientMsgId,
} from 'src/components/SupportCase/useStartCase';
import { useAgentHealth } from 'src/components/SupportCase/useSupportCases';
import useCaseResume from 'src/components/SupportCase/useCaseResume';
import { Z_INDEX } from 'src/constants/zIndex';
import { setAssistantPanelOpen } from 'src/utils/assistantPanel';
import type {
  CaseSummary,
  SavedPrompt,
} from 'src/components/SupportCase/types';

/**
 * Modals opened from inside the panel must stack above it (ENG-4272).
 *
 * The panel is `position: fixed` at `Z_INDEX.MAX` so the tour cannot bury it, but MUI
 * renders dialogs at the theme's `zIndex.modal` (1300 by default). A preview opened from
 * a document chip therefore painted *underneath* the panel: the dialog existed, and the
 * right third of the report and its actions row were covered.
 *
 * Raising it here, for this subtree only, keeps the change local: every other dialog in
 * the product keeps the default layer and the panel stays above the content behind it.
 */
export const withPanelZIndex = (base: Theme): Theme =>
  createTheme(base, { zIndex: { modal: Z_INDEX.MAX + 1 } });

interface SupportCasePanelProps {
  onClose: () => void;
  /** False keeps the panel mounted but hidden, so a draft and the case survive a close. */
  open?: boolean;
  /**
   * Open straight onto an existing case (ENG-4185).
   *
   * Used when a scheduled run finishes: Thor writes a notification, the notification
   * socket delivers it, and the launcher opens this panel with the case the run
   * produced. Without it the user is told an answer exists and then has to go and
   * find it.
   */
  initialCaseId?: number | null;
}

/**
 * The floating support panel (ENG-4137, decision 4A).
 *
 * Non-modal by design: the page underneath stays readable and scrollable, because
 * the user is usually asking about something they can see. It is a card, not a
 * dialog — no backdrop, no focus trap.
 *
 * The first message creates the case (that is when context is captured); after that
 * the same `CaseStream` the full page renders takes over, so there is exactly one
 * implementation of the conversation.
 */
const SupportCasePanel: FC<SupportCasePanelProps> = ({
  onClose,
  open = true,
  initialCaseId = null,
}) => {
  const [caseId, setCaseId] = useState<number | null>(initialCaseId);
  const [fullscreen, setFullscreen] = useState(false);
  const [caseSummary, setCaseSummary] = useState<CaseSummary | null>(null);
  // ENG-4272 — a dialog opened from inside this panel must stack above it; see
  // `withPanelZIndex`.
  const theme = useTheme();
  const panelTheme = useMemo(() => withPanelZIndex(theme), [theme]);
  const { health, isLoading: healthLoading } = useAgentHealth();
  // ENG-4211 — the create-case call now lives in one hook, shared with the hub's
  // Assistant tab. The panel's behaviour is unchanged: it stays on this conversation.
  const { send, created, suggested, creating, error } = useStartCase();
  // ENG-4251 (PA-09) — reopening must find the conversation for this page.
  const resume = useCaseResume(open && caseId === null && created === null);

  useEffect(() => {
    if (
      open &&
      caseId === null &&
      resume.state === 'ready' &&
      resume.resumeCaseId
    ) {
      setCaseId(resume.resumeCaseId);
    }
  }, [open, caseId, resume.state, resume.resumeCaseId]);

  // ENG-4220 — a chip is the same call as typing the text: one message, one new case.
  const handleRunPrompt = useCallback(
    (prompt: SavedPrompt) => {
      void send(prompt.body, newClientMsgId(), []);
    },
    [send]
  );

  // The launcher can hand the panel a different case while it is open (a second
  // scheduled run finished). Following it is the expected behaviour; ignoring it
  // would leave the panel on the older case with no sign anything happened.
  useEffect(() => {
    if (initialCaseId !== null) {
      setCaseId(initialCaseId);
    }
  }, [initialCaseId]);

  useEffect(() => {
    if (created) {
      setCaseId(created.id);
    }
  }, [created]);

  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    // document, never window: the listener outlives the component otherwise.
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleEscape]);

  /**
   * PA-19 — the panel is rendered into a host *after* `#root`.
   *
   * It is `position: fixed` with the top z-index, but the onboarding tour portals its
   * own layer and a later portal wins on DOM order regardless of the z-index we set.
   * Appending our host after the app root puts the panel above the tour mask for good,
   * instead of depending on which component mounted last.
   */
  const host = useMemo<HTMLElement | null>(() => {
    if (typeof document === 'undefined') {
      return null;
    }
    let node = document.getElementById('assistant-panel-host');
    if (!node) {
      node = document.createElement('div');
      node.id = 'assistant-panel-host';
      document.body.appendChild(node);
    }
    return node;
  }, []);

  // The tour reads this and refuses to advance (PA-19).
  useEffect(() => {
    setAssistantPanelOpen(open);
    return () => setAssistantPanelOpen(false);
  }, [open]);

  const dimensions = fullscreen
    ? {
        top: 24,
        right: 24,
        bottom: 24,
        left: 24,
        width: 'auto',
        height: 'auto',
      }
    : {
        bottom: 24,
        right: 24,
        /*
          ENG-4299 — 420 px is the width every header decision is measured at, but a
          fixed 420 overflows a window narrower than 468 px. The min() keeps the
          panel inside the viewport and leaves the same 24 px on both sides; at
          1440 px it is still exactly 420.
        */
        width: 'min(420px, calc(100vw - 48px))',
        height: 'min(620px, calc(100vh - 48px))',
      };

  const panel = (
    <ThemeProvider theme={panelTheme}>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          zIndex: Z_INDEX.MAX,
          display: open ? 'flex' : 'none',
          flexDirection: 'column',
          overflow: 'hidden',
          ...dimensions,
        }}
        data-testid="support-case-panel"
        role="complementary"
        aria-label="Pronto Assistant"
      >
        {caseId === null ? (
          <>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 2, py: 1.5 }}
            >
              <Box>
                <Typography variant="subtitle1">Pronto Assistant</Typography>
                <Typography variant="caption" color="text.secondary">
                  {caseSummary?.title || 'Ask the Pronto Assistant'}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={onClose}
                aria-label="Close the Pronto Assistant"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
            <Box sx={{ px: 2, pb: 2, flexGrow: 1, overflowY: 'auto' }}>
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  {resume.state === 'empty'
                    ? `No conversations about ${resume.contextLabel} yet. Ask below and the Assistant will look at it with you, propose a fix you can confirm, or send it to Pronto Housing Support.`
                    : 'Tell us what is going on. The Assistant will look at it with you, propose a fix you can confirm, or send it to Pronto Housing Support.'}
                </Typography>
                {resume.state === 'chooser' ? (
                  <Stack spacing={1} data-testid="case-chooser">
                    <Typography variant="subtitle2">
                      Your conversations here
                    </Typography>
                    {resume.open.map((row) => (
                      <Stack
                        key={row.id}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" noWrap>
                            {row.title}
                          </Typography>
                          <CaseContextLine
                            certification={row.certification}
                            property={row.property}
                            compact
                          />
                        </Box>
                        <Button size="small" onClick={() => setCaseId(row.id)}>
                          Resume
                        </Button>
                      </Stack>
                    ))}
                  </Stack>
                ) : null}
                {resume.earlier.length > 0 && caseId === null ? (
                  <Stack spacing={1} data-testid="earlier-conversations">
                    <Typography variant="subtitle2">
                      Earlier conversations
                    </Typography>
                    {resume.earlier.slice(0, 3).map((row) => (
                      <Button
                        key={row.id}
                        size="small"
                        sx={{ justifyContent: 'flex-start' }}
                        onClick={() => setCaseId(row.id)}
                      >
                        {row.title}
                      </Button>
                    ))}
                  </Stack>
                ) : null}
                {error ? <Alert severity="error">{error}</Alert> : null}
                <Composer
                  onSend={send}
                  disabled={creating}
                  health={health}
                  healthLoading={healthLoading}
                  footer={
                    <PromptFooter
                      onRun={handleRunPrompt}
                      offer={suggested}
                      disabled={creating}
                    />
                  }
                />
              </Stack>
            </Box>
          </>
        ) : (
          <CaseStream
            caseId={caseId}
            health={health}
            healthLoading={healthLoading}
            /* ENG-4299 — the panel is 420 px wide, so its header stacks: title row,
               then a meta row where only the context line truncates. */
            headerVariant="stacked"
            /* The offer arrives on the create response, which is also the moment the
             panel switches to the conversation. It rides the switch so the nudge is
             answerable instead of being unmounted with the composer. */
            promptOffer={suggested}
            fullscreen={fullscreen}
            onToggleFullscreen={() => setFullscreen((current) => !current)}
            onClose={onClose}
            onCaseChange={setCaseSummary}
          />
        )}
      </Paper>
    </ThemeProvider>
  );

  return host ? createPortal(panel, host) : panel;
};

export default SupportCasePanel;
